import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface Slider {
  _id: string;
  imageUrl: string;
  redirectionUrl?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class SliderService {
  async getSliders() {
    const response = await apiClient.get<ApiResponse<Slider[]>>('/slider/admin/get_all');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch sliders');
    }
    return response.data.data;
  }

  async getSliderById(id: string) {
    const response = await apiClient.get<ApiResponse<Slider>>(`/slider/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch slider');
    }
    return response.data.data;
  }

  async createSlider(data: Partial<Slider> & { image?: File }) {
    const formData = new FormData();
    
    // Append image file if provided
    if (data.image) {
      formData.append('image', data.image);
    }
    
    // Append other fields
    if (data.redirectionUrl !== undefined) {
      formData.append('redirectionUrl', data.redirectionUrl);
    }
    if (data.order !== undefined) {
      formData.append('order', data.order.toString());
    }
    if (data.isActive !== undefined) {
      formData.append('isActive', data.isActive.toString());
    }
    
    const response = await apiClient.post<ApiResponse<Slider>>('/slider/add', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create slider');
    }
    return response.data.data;
  }

  async updateSlider(id: string, data: Partial<Slider> & { image?: File }) {
    const formData = new FormData();
    
    // Append image file if provided
    if (data.image) {
      formData.append('image', data.image);
    }
    
    // Append other fields
    if (data.redirectionUrl !== undefined) {
      formData.append('redirectionUrl', data.redirectionUrl);
    }
    if (data.order !== undefined) {
      formData.append('order', data.order.toString());
    }
    if (data.isActive !== undefined) {
      formData.append('isActive', data.isActive.toString());
    }
    
    const response = await apiClient.put<ApiResponse<Slider>>(`/slider/update/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update slider');
    }
    return response.data.data;
  }

  async deleteSlider(id: string) {
    const response = await apiClient.delete<ApiResponse<any>>(`/slider/delete_by_id?id=${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete slider');
    }
    return response.data;
  }

  async toggleSlider(id: string) {
    const response = await apiClient.patch<ApiResponse<Slider>>(`/slider/toggle/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle slider');
    }
    return response.data.data;
  }
}

export const sliderService = new SliderService();
export default sliderService;

